import {User} from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {BackendApi} from "../../../../../api/api.ts";
import {useEffect, useState} from "react";
import {BusinessTable} from "./BusinessesTable.tsx";
import WideDrawer from "../../../../../components/general/WideDrawer.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import DailyBalanceGraph from "../../../entrepreneur/pages/BalancePage/DailyBalanceGraph.tsx";
import LoadingFrame from "../../../../../components/LoadingFrame.tsx";
import {Tab, Tabs} from "../../../../../components/general/Tabs.tsx";
import {Field, Label, Switch} from "@headlessui/react";
import {confirming} from "../../../../../lib/confirming.ts";
import {AdminBusinessFlagsService} from "../../../../../lib/Services/AdminBusinessFlagsService.ts";
import Dropdown from "../../../../../components/general/Dropdown.tsx";
import {BusinessInsights} from './BusinessInsights';
import {BusinessDocuments} from "./BusinessDocuments.tsx";
import CashflowStatement from "../../../../../components/specific/CashflowStatement.tsx";
import {FeatureFlag, FeatureFlagAudience, FeatureFlagGroup, groupFlagsByAudience} from "./featureFlagAudience.ts";
import {cn} from "../../../../../lib/utils.ts";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

export interface Business {
    id: string
    name: string
    legal_entity_type: LegalEntityType
    registration_number: string
    fte_amount: number
    industry: string
    description: string
    managed_by: string | null
    transaction_history_cutoff_date: {
        cutoff_date: string | null
    }
}

interface Props {
    currentUser: User
}

export default function BusinessesOverviewPage({currentUser}: Props) {
    const api = new BackendApi(currentUser.getToken())

    const [businesses, setBusinesses] = useState<Array<Business>>([])
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null)

    async function onLoad() {
        const reviewTransactions = await api.get('admin/businesses', {})
        const response = await reviewTransactions.json()
        setBusinesses(response)
    }

    useEffect(() => {
        onLoad()
    }, []);

    return (
        <>
            <WideDrawer
                title={""}
                open={(!!selectedBusiness)}
                updateOpenState={(state) => {
                    if (state)
                        return
                    setSelectedBusiness(null)
                }}

            >
                <div className="flex items-center gap-x-3">
                    <SecondaryButton
                        title={'Previous'}
                        onClick={() => setSelectedBusiness((prev) => {
                            if (!prev) return prev
                            if (businesses.length === 0) return prev
                            const currentIndex = businesses.indexOf(prev)
                            if (currentIndex === -1) return prev
                            if (currentIndex === 0) return businesses[businesses.length - 1]
                            return businesses[currentIndex - 1]
                        })}
                    />
                    <span>
                        {businesses.indexOf(selectedBusiness) + 1} of {businesses.length}
                    </span>
                    <SecondaryButton
                        title={'Next'}
                        onClick={() => setSelectedBusiness((prev) => {
                            if (!prev) return prev
                            if (businesses.length === 0) return prev
                            const currentIndex = businesses.indexOf(prev)
                            if (currentIndex === -1) return prev 
                            if (currentIndex === businesses.length - 1)
                                return businesses[0] 
                            return businesses[currentIndex + 1]
                        })}
                    />
                    <Dropdown selected={({value: selectedBusiness?.id + ' ' + selectedBusiness?.name || ''})}
                              options={businesses.map((business) => ({value: business.id + ' ' + business.name}))}
                              onUpdateFunc={
                                  (value: string) => {
                                      setSelectedBusiness(businesses.find(business => business.id + ' ' + business.name === value))
                                  }
                              }/>
                </div>
                <div>
                    <h1 className="text-2xl font-bold leading-6 text-gray-900 mt-12 mb-6 ">{selectedBusiness?.name}</h1>
                </div>
                <Tabs>
                    <Tab label={'Bank Accounts'}>
                        <BusinessBalance
                            business={selectedBusiness}
                            api={api}
                        />
                    </Tab>
                    <Tab label={'Flags'}>
                        <BusinessFlags business={selectedBusiness} api={api}/>
                    </Tab>
                    <Tab label={'Insights'}>
                        <BusinessInsights business={selectedBusiness} api={api}/>
                    </Tab>
                    <Tab label={'Cashflow'}>
                        <CashflowStatement business={selectedBusiness} api={api}/>
                    </Tab>
                    <Tab label={'Documents'}>
                        <BusinessDocuments business={selectedBusiness} api={api}/>
                    </Tab>
                </Tabs>
                <div>

                </div>
            </WideDrawer>
            <Shell currentUser={currentUser} isLoading={false}>
                <h1 className="weight-600 text-3xl mb-6 ">Businesses</h1>
                <BusinessTable data={businesses} onOpenClick={(business) => setSelectedBusiness(business)}/>
            </Shell>
        </>
    )
}


function BusinessFlags({
                           business, api
                       }: {
    business: Business, api
        :
        BackendApi
}) {
    const [flags, setFlags] = useState<FeatureFlag[]>([])

    const businessFlagsService = new AdminBusinessFlagsService(api)

    async function load() {
        const response = await businessFlagsService.getAllFlags(business.id)

        setFlags(Object.entries(response).map(([name, value]) => ({name, value})))
    }

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        setFlags([])
        load();
    }, [business]);

    const groups = groupFlagsByAudience(flags)
    const partner = groups.find((group) => group.audience === FeatureFlagAudience.PARTNER)
    const entrepreneur = groups.find((group) => group.audience === FeatureFlagAudience.ENTREPRENEUR)
    const general = groups.find((group) => group.audience === FeatureFlagAudience.GENERAL)

    function toggleFlag(name: string, value: boolean) {
        confirming('Are you sure you want to change this flag?', () => {
            businessFlagsService.setFlag(business?.id, name, !value).then(() => {
                load()
            })
        }, () => {
        })
    }

    return (
        <LoadingFrame isLoading={!flags.length}>
            {(partner || entrepreneur) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                    {partner && <FlagGroupSection group={partner} onToggle={toggleFlag} />}
                    {entrepreneur && <FlagGroupSection group={entrepreneur} onToggle={toggleFlag} />}
                </div>
            )}
            {general && (
                <div className={cn((partner || entrepreneur) && "mt-8", "max-w-lg")}>
                    <FlagGroupSection group={general} onToggle={toggleFlag} />
                </div>
            )}
        </LoadingFrame>
    )

}

function FlagGroupSection({
    group,
    onToggle,
}: {
    group: FeatureFlagGroup
    onToggle: (name: string, value: boolean) => void
}) {
    return (
        <section className="min-w-0">
            <h3 className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-2 font-semibold">{group.label}</h3>
            {group.flags.map(({name, value}) => (
                <Field key={name} className="flex items-center justify-between gap-4 mt-4">
                    <span className="flex grow flex-col min-w-0">
                        <Label as="span" passive className="text-sm/6 font-medium text-gray-900 break-all">
                            {name}
                        </Label>
                    </span>
                    <Switch
                        checked={value}
                        onChange={() => onToggle(name, value)}
                        className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 data-[checked]:bg-primary"
                    >
                        <span
                            aria-hidden="true"
                            className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                        />
                    </Switch>
                </Field>
            ))}
        </section>
    )
}

function BusinessBalance({business, api}: { business: Business, api: BackendApi }) {

    const [bankAccounts, setBankAccounts] = useState<Array<any>>([]);

    async function load() {
        const response = await api.get('admin/business/bank-accounts', {
            business_id: business.id
        })

        const bankAccountsResponse = await response.json()

        setBankAccounts(bankAccountsResponse)
    }

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        setBankAccounts([]);
        load();
    }, [business]);

    return (
        <>
            {bankAccounts?.length === 0 && <div className="text-center">No bank accounts found</div>}
            {bankAccounts?.map((bankAccount) => (
                <div>
                    <h2 className={"text-2xl mt-8 mb-4"}>{bankAccount.owner_name} - {bankAccount.iban}</h2>
                    <BalanceGraph business={business} api={api} bankAccountId={bankAccount.account_id}/>
                </div>
            ))}
        </>


    )
}


function BalanceGraph({business, api, bankAccountId}: { business: Business, api: BackendApi, bankAccountId: string }) {
    const [data, setData] = useState<any>();

    async function load() {
        console.log(new Date().toISOString())
        const data = await api.get('admin/graph/business/get-daily-balance-with-forecast', {
            business_id: business.id,
            bank_account_id: bankAccountId,
            start_date: new Date(business.transaction_history_cutoff_date.cutoff_date).toISOString().split('T')[0] || '2022-01-01T00:00:00',
            end_date: new Date().toISOString().split('T')[0],
        })

        const response = await data.json()
        setData(response)
    }

    useEffect(() => {
        load();
    }, []);

    return (
        <LoadingFrame isLoading={!data}>
            <DailyBalanceGraph data={data || []} className={"h-[60vh]"}></DailyBalanceGraph>
        </LoadingFrame>
    )
}
